import {
    Component,
    Element,
    Host,
    Listen,
    Prop,
    State,
    h,
} from "@stencil/core";
import { Status } from "../status-label/status";
import {
    ModalForm,
    ModalMessageSuccess,
    ModalMessageError,
} from "./modal/software-updates-modal";

import {
    getTranslateFn,
    TranslateFn,
    Translations,
} from "../stateful-content/stateful-content-utils";
import { SubscriptionInterface, FormGetInterface } from "./interface";

@Component({
    tag: "ux-iws-software-updates",
    styleUrl: "software-updates.scss",
})
export class SoftwareUpdates {
    @Element() element: HTMLUxIwsSoftwareUpdatesElement;

    private baseClass = "ux-iws-software-updates";
    private translate: TranslateFn;
    private modalForm: HTMLFormElement | null = null;
    private machineNumber: string = "";
    private machineName: string = "";

    /** Software Updates main status */
    @State() state: Status | null = null;

    /** Endpoint to check for software updates */
    @Prop() url: string;

    /** Endpoint to obtain machine information for the form */
    @Prop() urlFormGetApi: string;

    /** Endpoint form submission  */
    @Prop() urlFormPostApi: string;

    /** Tooltip link */
    @Prop() tooltipLink: string;

    /** Read more link */
    @Prop() readMoreLink: string;

    /** Privacy policy link */
    @Prop() privacyPolicyLink: string;

    /** JSON string or object with translations */
    @Prop() translations: Translations | string = "";

    /** Modal form open state */
    @State() modalFormState: boolean = false;

    /** Modal message success open state */
    @State() modalMessageSuccessState: boolean = false;

    /** Modal message error open state */
    @State() modalMessageErrorState: boolean = false;

    /** Software updates state */
    @State() softwareUpdateState: boolean = false;

    /** Modal form spinner get state */
    @State() modalFormSpinnerGetState: boolean = false;

    /** Modal form spinner post state */
    @State() modalFormSpinnerPostState: boolean = false;

    /** Handle modals */
    @Listen("uxModalClose", { target: "window" })
    modalClose(event: CustomEvent) {
        if (event.detail === "software-updates-modal-form") {
            this.modalFormState = false;
        }
        if (event.detail === "software-updates-modal-message-success") {
            this.modalMessageSuccessState = false;
        }
        if (event.detail === "software-updates-modal-message-error") {
            this.modalMessageErrorState = false;
        }
    }

    /** Handle form validation */
    private onFormValidated(event: CustomEvent) {
        event.preventDefault();
        event.detail.isValid &&
            this.handleFormSubmission(event.detail.serializedFormData);
    }

    /** Handle switch input */
    @Listen("uxChange", { target: "window" })
    switchChange(event: CustomEvent) {
        this.softwareUpdateState = event.detail;
        if (!event.detail) {
            const checkbox = this.element.querySelector(
                ".ux-iws-software-updates__legal-checkbox"
            ) as HTMLUxCheckboxElement | null;
            if (checkbox !== undefined) {
                checkbox.checked = false;
                checkbox.error = false;
            }
        }
    }

    componentWillLoad() {
        this.translate = getTranslateFn(this.translations);
        this.fetchDataMain(this.url);
    }

    /** Get */
    private fetchDataMain = async (url: string) => {
        try {
            if (url.includes("mock-data")) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }
            const response = await fetch(url);
            const data: SubscriptionInterface = await response.json();
            if (response.ok) {
                this.state = data.state.toLowerCase() as Status;
                return data;
            }
        } catch (error) {
            console.warn(error);
        }
    };

    private fetchDataModal = async () => {
        this.modalFormState = true;
        this.modalFormSpinnerGetState = true;
        try {
            if (this.urlFormGetApi.includes("mock-data")) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }
            const response = await fetch(this.urlFormGetApi);
            const data: FormGetInterface = await response.json();
            if (response.ok) {
                this.softwareUpdateState = data.securityUpdateSubscribed;
                this.machineNumber = data.machineNumber;
                this.machineName = data.machineName;
                this.modalFormSpinnerGetState = false;
                return data;
            } else {
                this.modalMessageErrorState = true;
                this.modalFormSpinnerGetState = false;
            }
        } catch (error) {
            this.modalMessageErrorState = true;
            this.modalFormSpinnerGetState = false;
        }
    };

    /** Send request and handle response */
    private handleFormSubmission = async (serializedFormData) => {
        this.modalMessageSuccessState = false;
        this.modalMessageErrorState = false;
        this.modalFormSpinnerPostState = true;
        const urlSearchParams = new URLSearchParams(serializedFormData);
        const formData = {};
        for (const [key, value] of urlSearchParams.entries()) {
            formData[key] =
                value === "true" ? true : value === "false" ? false : value;
        }
        try {
            if (this.urlFormPostApi.includes("mock-data")) {
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }
            const options = this.urlFormPostApi.includes("mock-data")
                ? {}
                : {
                      method: "POST",
                      body: JSON.stringify(formData),
                      headers: {
                          "Content-Type": "application/json",
                      },
                  };
            const response = await fetch(this.urlFormPostApi, options);
            if (response.ok) {
                this.modalFormState = false;
                this.modalMessageSuccessState = true;
                this.modalFormSpinnerPostState = false;
                this.fetchDataMain(this.url);
            } else {
                this.modalFormState = false;
                this.modalMessageErrorState = true;
                this.modalFormSpinnerPostState = false;
            }
        } catch (error) {
            this.modalFormState = false;
            this.modalMessageErrorState = true;
            this.modalFormSpinnerPostState = false;
        }
    };

    private renderModalForm = () => {
        const modalOptions = {
            formElement: this.modalForm,
            modalFormState: this.modalFormState,
            modalFormSpinnerGetState: this.modalFormSpinnerGetState,
            modalFormSpinnerPostState: this.modalFormSpinnerPostState,
            machineNumber: this.machineNumber,
            machineName: this.machineName,
            softwareUpdateState: this.softwareUpdateState,
            readMoreLink: this.readMoreLink,
            privacyPolicyLink: this.privacyPolicyLink,
            translate: this.translate,
            onFormValidated: (e: any) => this.onFormValidated(e),
        };
        return ModalForm(modalOptions);
    };

    private renderModalSuccessMessage = () => {
        const modalOptions = {
            modalMessageSuccessState: this.modalMessageSuccessState,
            translate: this.translate,
        };
        return ModalMessageSuccess(modalOptions);
    };

    private renderModalErrorMessage = () => {
        const modalOptions = {
            modalMessageErrorState: this.modalMessageErrorState,
            translate: this.translate,
        };
        return ModalMessageError(modalOptions);
    };

    render() {
        const colClass =
            this.state === "unsubscribed"
                ? "ux-col-12 ux-col-md-6"
                : "ux-col-12";

        const allowedStatusValues = [
            Status.INSTALLATION_FAILED,
            Status.SUBSCRIBED,
            Status.UNSUBSCRIBED,
            Status.UPDATE_AVAILABLE,
        ];

        return (
            this.state !== null &&
            allowedStatusValues.includes(this.state) && (
                <Host class={this.baseClass}>
                    <div class="ux-row">
                        <div class={colClass}>
                            <span class="ux-info-box__headline ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">
                                {this.translate("label")}
                            </span>
                            {this.state !== "unsubscribed" && (
                                <button
                                    class="ux-icon-button ux-u-ml-xxxs"
                                    onClick={this.fetchDataModal}
                                    data-testid="open-software-updates-modal"
                                >
                                    <ux-icon name="cog"></ux-icon>
                                </button>
                            )}
                            <ux-icon
                                name="info"
                                class="ux-u-ml-xxxs"
                                data-testid="info"
                            ></ux-icon>
                            <ux-tooltip
                                tooltip-target="ux-icon[name='info']"
                                align="bottom"
                            >
                                <div>
                                    <p class="ux-text-normal ux-u-mt-zero">
                                        {this.translate("tooltip.copy")}
                                    </p>
                                    <a
                                        href={this.tooltipLink}
                                        target="_blank"
                                        class="ux-text-link ux-text-link--green"
                                    >
                                        {this.translate("tooltip.linkText")}
                                        <ux-icon name="arrow-small-right" />
                                    </a>
                                </div>
                            </ux-tooltip>
                            <ux-iws-status-label type={this.state}>
                                {this.translate(
                                    "state." + this.state + ".label"
                                )}
                            </ux-iws-status-label>
                        </div>
                        {this.state === "unsubscribed" && (
                            <div class="ux-col-12 ux-col-md-6">
                                <div class="ux-u-mt-xxxs ux-u-mt-md-zero">
                                    <ux-button
                                        class="ux-iws-software-updates__modal-trigger"
                                        icon="cog"
                                        onClick={this.fetchDataModal}
                                        data-testid="open-software-updates-modal"
                                    >
                                        {this.translate(
                                            "state." + this.state + ".button"
                                        )}
                                    </ux-button>
                                </div>
                            </div>
                        )}
                    </div>
                    {this.renderModalForm()}
                    {this.renderModalSuccessMessage()}
                    {this.renderModalErrorMessage()}
                </Host>
            )
        );
    }
}
